import React from 'react';
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ForwardIcon from '@mui/icons-material/Forward';
import { money } from './format.js';
import { varianceDriverLabel } from './varianceSummaryTableModel.js';

const fileFindingCount = (finding) => (
  Number(finding?.errors || 0) + Number(finding?.warnings || 0)
);

// Narrow-viewport fallback for the variance summary. Each row becomes a
// tap-to-open card with the same fields as the desktop table.
const VarianceCardList = ({ rows, onRowClick, fileHealthByBac = {} }) => (
  <Stack spacing={1}>
    {rows.map((row) => {
      const reviewed = row.review?.reviewed;
      const healthFinding = fileHealthByBac[String(row.bac)];
      const accountSummary = row.accountMatchSummary || {};
      const dealerDisplayName = row.dealerDisplayName || row.dealerName || accountSummary.displayName || '—';
      const findingCount = fileFindingCount(healthFinding);
      return (
        <Paper
          key={row.bac}
          variant="outlined"
          onClick={() => onRowClick(row.bac)}
          sx={{
            p: 1.75,
            cursor: 'pointer',
            borderRadius: 1.5,
            opacity: reviewed ? 0.8 : 1,
            transition: 'border-color 120ms, transform 120ms',
            '&:hover': { borderColor: 'primary.main', transform: 'translateY(-1px)' },
          }}
        >
          <Stack spacing={0.75}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                {dealerDisplayName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                {row.brand || '—'}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              BAC {row.bac} · File findings: {findingCount}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 800 }}>
                {varianceDriverLabel(row)}
                {row.varianceDriver?.detail ? ` · ${row.varianceDriver.detail}` : ''}
              </Typography>
              {row.review?.inheritedFromRound && (
                <Chip
                  size="small"
                  icon={<ForwardIcon sx={{ fontSize: 14 }} />}
                  label={`From R${row.review.inheritedFromRound}`}
                  variant="outlined"
                  sx={{ color: 'info.main', borderColor: 'info.main' }}
                />
              )}
              {row.dpeEnrollmentStatusCd && (
                <Chip
                  size="small"
                  label={`DPE ${row.dpeEnrollmentStatusCd}`}
                  color={String(row.dpeEnrollmentStatusCd).toUpperCase() === 'ENROLLED' ? 'success' : 'warning'}
                  variant="outlined"
                />
              )}
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" spacing={1}>
              {[
                { label: 'GM', value: row.gmTotal, accent: 'primary.main' },
                { label: 'CPQ', value: row.cpqTotal, accent: 'secondary.main' },
                { label: 'DPE', value: row.dpeTotal, accent: 'info.main' },
                { label: 'AppSheet', value: row.appSheetTotal ?? row.app_sheet_total, accent: 'warning.main' },
              ].map((tile) => (
                <Box
                  key={tile.label}
                  sx={{
                    flex: 1,
                    p: 0.75,
                    borderRadius: 0.75,
                    bgcolor: 'action.hover',
                    borderLeft: '2px solid',
                    borderColor: tile.accent,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.3, fontSize: 10 }}>
                    {tile.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {money(tile.value)}
                  </Typography>
                </Box>
              ))}
            </Stack>
            {reviewed && (
              <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: 0.25 }}>
                <CheckCircleIcon fontSize="inherit" color="success" />
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700, ml: 0.5 }}>
                  Reviewed
                </Typography>
              </Stack>
            )}
          </Stack>
        </Paper>
      );
    })}
  </Stack>
);

export default VarianceCardList;
